import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { vacationsAPI, absencesAPI, employeesAPI, locationsAPI, feiertageAPI, schulferienAPI } from '../lib/api';
import { formatLastFirst } from '../lib/nameFormat';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
} from 'date-fns';
import { toast } from 'sonner';
import { EVENT_COLORS } from './team-calendar/constants';
import { CalendarHeader, CalendarMonthNav } from './team-calendar/CalendarHeader';
import { CalendarGrid } from './team-calendar/CalendarGrid';
import { DayDetailDialog, CalendarLegend } from './team-calendar/DayDetailDialog';

export default function TeamCalendar({ embedded = false, restrictToIds = null }) {
  const { hasPermission } = useAuth();
  const canManageVacations = hasPermission('vacations', 'execute');
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [companyFreeDays, setCompanyFreeDays] = useState([]);
  const [schulferien, setSchulferien] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(() => {
    const d = searchParams.get('date');
    if (d) {
      const parsed = new Date(`${d}T00:00:00`);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [highlightEmpId, setHighlightEmpId] = useState(() => searchParams.get('emp') || null);
  const scrollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const [vacRes, absRes, empRes, locRes, holRes, freeDaysRes, ferienRes, bdayRes] = await Promise.all([
        vacationsAPI.getAll(),
        absencesAPI.getAll(),
        employeesAPI.getAll({ is_active: true }),
        locationsAPI.getAll(),
        feiertageAPI.getFlat(year),
        feiertageAPI.getCompanyFreeDays(year),
        schulferienAPI.getAll(year),
        api.get('/dashboard/birthdays?days=365').catch(() => ({ data: { birthdays: [] } })),
      ]);
      setVacations(vacRes.data || []);
      setAbsences(absRes.data || []);
      setEmployees(empRes.data || []);
      setLocations(locRes.data || []);
      setHolidays(holRes.data || []);
      setCompanyFreeDays(freeDaysRes.data || []);
      setSchulferien(ferienRes.data || []);
      setBirthdays(bdayRes.data?.birthdays || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aus dem Dashboard verlinkter Mitarbeiter: Zeile anscrollen + kurz hervorheben
  useEffect(() => {
    if (!highlightEmpId || loading) return;
    const t1 = setTimeout(() => {
      const row = document.querySelector(`[data-testid="emp-row-${highlightEmpId}"]`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    const t2 = setTimeout(() => setHighlightEmpId(null), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [highlightEmpId, loading]);

  const daysInMonth = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [currentDate]);

  const branches = useMemo(() => {
    const branchMap = {};
    locations.forEach(l => {
      if (l.branch_id && !branchMap[l.branch_id]) branchMap[l.branch_id] = l.branch_name;
    });
    return Object.entries(branchMap).map(([id, name]) => ({ id, name }));
  }, [locations]);

  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    if (restrictToIds) filtered = filtered.filter(e => restrictToIds.has(e.id));
    if (filterBranch !== 'all') filtered = filtered.filter(e => e.branch_id === filterBranch);
    if (filterLocation !== 'all') filtered = filtered.filter(e => e.location_id === filterLocation);
    return filtered.sort((a, b) => {
      const locCmp = (a.location_name || '').localeCompare(b.location_name || '');
      if (locCmp !== 0) return locCmp;
      return formatLastFirst(a).localeCompare(formatLastFirst(b));
    });
  }, [employees, filterLocation, filterBranch, restrictToIds]);

  const employeesByLocation = useMemo(() => {
    const groups = {};
    filteredEmployees.forEach(emp => {
      const locName = emp.location_name || 'Ohne Standort';
      if (!groups[locName]) groups[locName] = [];
      groups[locName].push(emp);
    });
    return groups;
  }, [filteredEmployees]);

  const eventsByEmployee = useMemo(() => {
    const map = {};
    vacations.forEach(v => {
      if (v.status === 'cancelled' || v.status === 'rejected') return;
      if (!map[v.employee_id]) map[v.employee_id] = [];
      map[v.employee_id].push({
        id: v.id,
        type: v.status === 'approved' ? 'vacation_approved' : 'vacation_pending',
        start: v.start_date,
        end: v.end_date,
        label: v.vacation_type,
        employeeName: v.employee_name,
        notes: v.notes,
        isRetroactive: !!v.is_retroactive,
      });
    });
    absences.forEach(a => {
      if (!map[a.employee_id]) map[a.employee_id] = [];
      const typeKey = a.absence_type === 'K' ? 'sick'
        : a.absence_type === 'KK' ? 'child_sick'
        : a.absence_type === 'EZ' ? 'parental_leave'
        : a.absence_type === 'KO' ? 'sick_no_note'
        : 'other_absence';
      map[a.employee_id].push({
        id: a.id,
        type: typeKey,
        start: a.start_date,
        end: a.end_date,
        label: a.absence_type_label || EVENT_COLORS[typeKey].label,
        employeeName: a.employee_name,
        notes: a.notes,
      });
    });
    return map;
  }, [vacations, absences]);

  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach(h => { map[h.date] = h.name; });
    companyFreeDays.forEach(h => { map[h.date] = h.name; });
    return map;
  }, [holidays, companyFreeDays]);

  // Birthdays gruppiert nach "DD-MM" (jahrunabhängig — erscheinen in allen Monaten)
  // Nur Mitarbeiter zeigen, die nach aktuellem Filter sichtbar sind.
  const birthdaysByDay = useMemo(() => {
    const filteredEmpIds = new Set(filteredEmployees.map(e => e.id));
    const map = {};
    birthdays.forEach(b => {
      if (!filteredEmpIds.has(b.id)) return;
      const key = `${b.day}-${b.month}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [birthdays, filteredEmployees]);

  const getSchulferienForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return schulferien.filter(f => dateStr >= f.start_date && dateStr <= f.end_date);
  };

  const getEventForDay = (employeeId, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const events = eventsByEmployee[employeeId] || [];
    return events.find(e => dateStr >= e.start && dateStr <= e.end);
  };

  const openDayDetails = (day) => {
    setSelectedDay(day);
    setDayDialogOpen(true);
  };

  const getEventsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const events = [];
    filteredEmployees.forEach(emp => {
      const empEvents = eventsByEmployee[emp.id] || [];
      empEvents.forEach(e => {
        if (dateStr >= e.start && dateStr <= e.end) events.push({ ...e, employee: emp });
      });
    });
    return events;
  };

  // Scroll to today on mount
  useEffect(() => {
    if (!loading && scrollRef.current) {
      const todayCol = scrollRef.current.querySelector('[data-today="true"]');
      if (todayCol) todayCol.scrollIntoView({ inline: 'center', behavior: 'smooth' });
    }
  }, [loading, currentDate]);

  // Tastatur-Shortcuts: ←/→ Monat, Pos1/Home = Heute (analog Outlook/Google)
  useEffect(() => {
    const onKeyDown = (e) => {
      // In Eingabefeldern, Selects oder Dialogen nicht reagieren
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Wenn ein Dialog/Sheet offen ist → bleibt offen, aber wir reagieren nicht
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentDate((d) => subMonths(d, 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentDate((d) => addMonths(d, 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setCurrentDate(new Date());
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="team-calendar-page">
        <CalendarHeader
          filteredEmployees={filteredEmployees}
          branches={branches}
          filterBranch={filterBranch}
          setFilterBranch={setFilterBranch}
          filterLocation={filterLocation}
          setFilterLocation={setFilterLocation}
          locations={locations}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
        />

        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40 pb-3 px-3 sm:px-6">
            <CalendarMonthNav currentDate={currentDate} setCurrentDate={setCurrentDate} />
          </CardHeader>

          <CardContent className="p-0">
            <CalendarGrid
              scrollRef={scrollRef}
              filteredEmployees={filteredEmployees}
              employeesByLocation={employeesByLocation}
              daysInMonth={daysInMonth}
              currentDate={currentDate}
              holidayMap={holidayMap}
              getSchulferienForDay={getSchulferienForDay}
              getEventForDay={getEventForDay}
              birthdaysByDay={birthdaysByDay}
              openDayDetails={openDayDetails}
              highlightEmpId={highlightEmpId}
            />
          </CardContent>
        </Card>

        <CalendarLegend />

        <DayDetailDialog
          open={dayDialogOpen}
          onOpenChange={setDayDialogOpen}
          selectedDay={selectedDay}
          holidayMap={holidayMap}
          getSchulferienForDay={getSchulferienForDay}
          getEventsForDay={getEventsForDay}
          birthdaysByDay={birthdaysByDay}
          canManageVacations={canManageVacations}
          onVacationChanged={loadData}
        />
      </div>
    </MaybeLayout>
  );
}
